import * as React from "react";
import Cropper, { type Area } from "react-easy-crop";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Loader2 } from "lucide-react";

/** Side length (px) of the square image produced by the crop. */
const OUTPUT_SIZE = 512;
const MIN_ZOOM = 1;
const MAX_ZOOM = 3;

/**
 * User-facing strings for the dialog, supplied by the caller so the component
 * stays translation-agnostic (mirrors ColorPickerDialog).
 */
export interface AvatarCropLabels {
  /** Dialog title */
  title: string;
  /** Description shown under the title */
  description: string;
  /** Accessible label for the zoom slider */
  zoom: string;
  /** Text for the confirm/save button */
  save: string;
  /** Text for the cancel button */
  cancel: string;
}

export interface AvatarCropDialogProps {
  /** Whether the dialog is open */
  open: boolean;
  /** Callback fired when the open state changes */
  onOpenChange: (open: boolean) => void;
  /** Object URL of the picked image to crop */
  imageSrc: string;
  /** MIME type of the source file; preserved in the cropped output */
  mimeType: string;
  /**
   * Fired with the square, cropped image once the user confirms. The dialog
   * stays open (and shows `busy`) while the caller uploads.
   */
  onCropped: (blob: Blob) => void;
  /** User-facing strings */
  labels: AvatarCropLabels;
  /** When true, disables actions and shows a spinner (upload in progress) */
  busy?: boolean;
}

/**
 * Loads an image element from a source URL.
 */
function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.addEventListener("load", () => resolve(img));
    img.addEventListener("error", (e) => reject(e));
    // Object URLs are same-origin, but set crossOrigin defensively so the
    // canvas is never tainted for http(s) sources.
    img.crossOrigin = "anonymous";
    img.src = src;
  });
}

/**
 * Draws the selected crop region onto a fixed-size square canvas and returns
 * it as a Blob in the given MIME type (transparency preserved for PNG).
 */
async function getCroppedBlob(imageSrc: string, cropPixels: Area, mimeType: string): Promise<Blob> {
  const image = await loadImage(imageSrc);
  const canvas = document.createElement("canvas");
  canvas.width = OUTPUT_SIZE;
  canvas.height = OUTPUT_SIZE;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Could not get 2D canvas context");

  ctx.drawImage(
    image,
    cropPixels.x,
    cropPixels.y,
    cropPixels.width,
    cropPixels.height,
    0,
    0,
    OUTPUT_SIZE,
    OUTPUT_SIZE
  );

  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("Canvas is empty"))),
      mimeType,
      0.92
    );
  });
}

/**
 * AvatarCropDialog — a modal circular crop/zoom tool for profile pictures.
 *
 * Drag to reposition, wheel/pinch or the slider to zoom. On confirm it emits a
 * square 512×512 Blob (via canvas) that the caller uploads.
 */
export function AvatarCropDialog({
  open,
  onOpenChange,
  imageSrc,
  mimeType,
  onCropped,
  labels,
  busy = false,
}: AvatarCropDialogProps) {
  return (
    <Dialog open={open} onOpenChange={(next) => (busy ? undefined : onOpenChange(next))}>
      <DialogContent className="max-w-sm" hideCloseButton={busy}>
        <DialogHeader>
          <DialogTitle>{labels.title}</DialogTitle>
          <DialogDescription>{labels.description}</DialogDescription>
        </DialogHeader>
        <AvatarCropForm
          imageSrc={imageSrc}
          mimeType={mimeType}
          onCropped={onCropped}
          onCancel={() => onOpenChange(false)}
          labels={labels}
          busy={busy}
        />
      </DialogContent>
    </Dialog>
  );
}

interface AvatarCropFormProps {
  imageSrc: string;
  mimeType: string;
  onCropped: (blob: Blob) => void;
  onCancel: () => void;
  labels: AvatarCropLabels;
  busy: boolean;
}

// Mounted fresh each time the dialog opens (Radix unmounts closed content), so
// crop/zoom state needs no reset effect.
function AvatarCropForm({
  imageSrc,
  mimeType,
  onCropped,
  onCancel,
  labels,
  busy,
}: AvatarCropFormProps) {
  const [crop, setCrop] = React.useState({ x: 0, y: 0 });
  const [zoom, setZoom] = React.useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = React.useState<Area | null>(null);
  const [saving, setSaving] = React.useState(false);

  const handleSave = async () => {
    if (!croppedAreaPixels) return;
    setSaving(true);
    try {
      const blob = await getCroppedBlob(imageSrc, croppedAreaPixels, mimeType);
      // Hands off to the caller, which flips `busy` to cover the upload phase.
      onCropped(blob);
    } catch (err) {
      console.error("avatar crop failed", err);
    } finally {
      setSaving(false);
    }
  };

  const disabled = busy || saving;

  return (
    <>
      <div className="space-y-4">
        <div className="relative h-64 w-full overflow-hidden rounded-md bg-muted">
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            aspect={1}
            cropShape="round"
            showGrid={false}
            minZoom={MIN_ZOOM}
            maxZoom={MAX_ZOOM}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={(_, pixels) => setCroppedAreaPixels(pixels)}
          />
        </div>
        <div className="space-y-1.5">
          <span className="text-xs font-medium text-muted-foreground">{labels.zoom}</span>
          <Slider
            min={MIN_ZOOM}
            max={MAX_ZOOM}
            step={0.01}
            value={[zoom]}
            onValueChange={([z]) => setZoom(z)}
            aria-label={labels.zoom}
            disabled={disabled}
          />
        </div>
      </div>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel} disabled={disabled}>
          {labels.cancel}
        </Button>
        <Button type="button" onClick={handleSave} disabled={disabled || !croppedAreaPixels}>
          {disabled ? <Loader2 className="h-4 w-4 animate-spin" /> : labels.save}
        </Button>
      </DialogFooter>
    </>
  );
}
